/**
 * Dogma (Settled): a marching system of answers.
 * Each peel changes the claim and the category it demands.
 * Wrong color grinds the current shell as a III (3 Answers);
 * matching category peels in one Word. Chip never peels. strongVs ignored.
 *
 * Scaffold:
 * - teach: Faith → Peace → Wisdom (3)
 * - full:  Wisdom → Faith → Peace → Wisdom (4)
 */
import type { Category } from "../board/types";
import type { Presence, VerseDef } from "../cards/types";

export type DogmaScaffold = "teach" | "full";

/** Off-type Answers grind the current shell like a III (3 remaining). */
export const DOGMA_SHELL_HP = 3;

export interface DogmaState {
  /** Current shell index (0 = outermost). */
  index: number;
  sequence: Category[];
  /** Type chip / demand revealed (Name). Resets on peel. */
  named: boolean;
  scaffold: DogmaScaffold;
  /**
   * Remaining III pips on the current shell for *off-type* Answers.
   * Correct category always peels. Resets to 3 on each new shell.
   */
  shellHp: number;
}

export const DOGMA_TEACH_SEQUENCE: readonly Category[] = [
  "faith",
  "peace",
  "wisdom",
];

export const DOGMA_FULL_SEQUENCE: readonly Category[] = [
  "wisdom",
  "faith",
  "peace",
  "wisdom",
];

const TEACH_CLAIMS = [
  "Leave this teaching and you leave God.",
  "Do not ask. The faithful already know.",
  "There is no further Word. Only stay.",
] as const;

const FULL_CLAIMS = [
  "Our word is His word. Do not look past it.",
  "Leave this teaching and you leave God.",
  "Do not ask. The faithful already know.",
  "There is no further Word. Only stay.",
] as const;

const TEACH_NAMES = ["The Warning", "The Hedge", "The Covering"] as const;
const FULL_NAMES = [
  "The Teaching",
  "The Warning",
  "The Hedge",
  "The Covering",
] as const;

export function dogmaSequence(scaffold: DogmaScaffold): Category[] {
  return scaffold === "full"
    ? [...DOGMA_FULL_SEQUENCE]
    : [...DOGMA_TEACH_SEQUENCE];
}

export function initDogmaState(scaffold: DogmaScaffold = "teach"): DogmaState {
  return {
    index: 0,
    sequence: dogmaSequence(scaffold),
    named: false,
    scaffold,
    shellHp: DOGMA_SHELL_HP,
  };
}

export function isDogmaPresence(p: Presence | null | undefined): boolean {
  return !!p && p.side === "enemy" && (p.lieType === "dogma" || !!p.dogma);
}

export function dogmaDemand(d: DogmaState): Category {
  return d.sequence[Math.min(d.index, d.sequence.length - 1)] ?? "wisdom";
}

export function dogmaIsCore(d: DogmaState): boolean {
  return d.index >= d.sequence.length - 1;
}

/** Shells still standing, including the current one. */
export function dogmaRemaining(d: DogmaState): number {
  return Math.max(1, d.sequence.length - d.index);
}

/** Map remaining shells to a 1–3 art / intensity band. */
export function dogmaArtLevel(d: DogmaState): 1 | 2 | 3 {
  const left = dogmaRemaining(d);
  if (left >= 3) return 3;
  if (left === 2) return 2;
  return 1;
}

export function dogmaClaim(d: DogmaState): string {
  const table = d.scaffold === "full" ? FULL_CLAIMS : TEACH_CLAIMS;
  return table[d.index] ?? table[table.length - 1]!;
}

export function dogmaName(d: DogmaState): string {
  const table = d.scaffold === "full" ? FULL_NAMES : TEACH_NAMES;
  return table[d.index] ?? "Settled";
}

export function dogmaMeta(d: DogmaState): string {
  const demand = dogmaDemand(d);
  const demandLabel = demand.charAt(0).toUpperCase() + demand.slice(1);
  const step = `${d.index + 1}/${d.sequence.length}`;
  const grind =
    d.shellHp < DOGMA_SHELL_HP ? ` · ${d.shellHp}/${DOGMA_SHELL_HP}` : "";
  if (d.named || dogmaIsCore(d)) {
    return `dogma · ${step} · wants ${demandLabel}${grind}`;
  }
  return `dogma · ${step}${grind}`;
}

export type DogmaHitKind = "answer" | "chip" | "castDown" | "name";

export interface DogmaHitResult {
  action: "peel" | "silence" | "name" | "miss" | "noop" | "grind";
  summary: string;
  silenced: boolean;
  peeled: boolean;
  ground?: boolean;
}

export function applyDogmaName(d: DogmaState): {
  state: DogmaState;
  result: DogmaHitResult;
} {
  return {
    state: { ...d, named: true },
    result: {
      action: "name",
      summary: `the demand is ${dogmaDemand(d)}`,
      silenced: false,
      peeled: false,
    },
  };
}

/**
 * One interaction with Settled.
 * - chip: never peels
 * - name: reveal demand only
 * - castDown: only the core
 * - answer: peels only when category === current demand (strongVs ignored)
 */
export function applyDogmaHit(
  d: DogmaState,
  opts: {
    kind: DogmaHitKind;
    category: Category;
  },
): { state: DogmaState; result: DogmaHitResult } {
  if (opts.kind === "name") {
    return applyDogmaName(d);
  }

  if (opts.kind === "chip") {
    return {
      state: d,
      result: {
        action: "noop",
        summary: "the system does not come apart in a crowd-clear",
        silenced: false,
        peeled: false,
      },
    };
  }

  if (opts.kind === "castDown") {
    if (!dogmaIsCore(d)) {
      return {
        state: d,
        result: {
          action: "miss",
          summary: "Cast down finds no exposed core",
          silenced: false,
          peeled: false,
        },
      };
    }
    return {
      state: d,
      result: {
        action: "silence",
        summary: "the core is cast down",
        silenced: true,
        peeled: true,
      },
    };
  }

  // answer — correct color peels; wrong color grinds this shell as a III
  if (opts.category === dogmaDemand(d)) {
    return peelDogmaShell(d, "the Word matches");
  }

  const hp = Math.max(0, (d.shellHp ?? DOGMA_SHELL_HP) - 1);
  if (hp <= 0) {
    return peelDogmaShell(
      { ...d, shellHp: 0 },
      `ground down without ${dogmaDemand(d)}`,
    );
  }
  return {
    state: { ...d, shellHp: hp, named: d.named },
    result: {
      action: "grind",
      summary: `wants ${dogmaDemand(d)} — shell ${hp}/${DOGMA_SHELL_HP}`,
      silenced: false,
      peeled: false,
      ground: true,
    },
  };
}

function peelDogmaShell(
  d: DogmaState,
  why: string,
): { state: DogmaState; result: DogmaHitResult } {
  if (dogmaIsCore(d)) {
    return {
      state: d,
      result: {
        action: "silence",
        summary: why,
        silenced: true,
        peeled: true,
      },
    };
  }
  const next: DogmaState = {
    ...d,
    index: d.index + 1,
    named: false,
    shellHp: DOGMA_SHELL_HP,
  };
  return {
    state: next,
    result: {
      action: "peel",
      summary: `${why} — now ${dogmaDemand(next)}`,
      silenced: false,
      peeled: true,
    },
  };
}

/** James 1:5-style: Name always; then peel or grind like any Answer. */
export function applyDogmaNameThenMaybePeel(
  d: DogmaState,
  category: Category,
): { state: DogmaState; result: DogmaHitResult; named: boolean } {
  const named = applyDogmaName(d);
  const hit = applyDogmaHit(named.state, { kind: "answer", category });
  const summary =
    hit.result.action === "grind"
      ? `named — ${hit.result.summary}`
      : hit.result.action === "name"
        ? hit.result.summary
        : hit.result.summary;
  return {
    ...hit,
    result: { ...hit.result, summary },
    named: true,
  };
}

export function verseNamesDogma(def: Pick<VerseDef, "nameLie">): boolean {
  return !!def.nameLie;
}

export function verseCastsDown(def: Pick<VerseDef, "castDown">): boolean {
  return !!def.castDown;
}

export function verseWipesConfusion(
  def: Pick<VerseDef, "wipeConfusion">,
): boolean {
  return !!def.wipeConfusion;
}

export type VestmentPipKind = "peeled" | "current" | "ahead";

/** One pip per shell — peeled / current (demand color) / still vested. */
export function dogmaVestmentPips(
  d: DogmaState,
): { kind: VestmentPipKind; demand: Category }[] {
  return d.sequence.map((demand, i) => ({
    kind: i < d.index ? "peeled" : i === d.index ? "current" : "ahead",
    demand,
  }));
}

export function dogmaDemandLabel(d: DogmaState): string {
  const c = dogmaDemand(d);
  return c.charAt(0).toUpperCase() + c.slice(1);
}

/** Off-type grind has started — show III pips on the current shell. */
export function dogmaShellIsGrinding(d: DogmaState): boolean {
  return (d.shellHp ?? DOGMA_SHELL_HP) < DOGMA_SHELL_HP;
}

/** Instant that can peel a Settled shell (category must still match). */
export function isDogmaPeelAnswer(def: Pick<VerseDef, "kind" | "selector" | "answerLevels">): boolean {
  if (def.kind !== "instant") return false;
  if (!def.answerLevels) return false;
  if (def.selector === "layerChip" || def.selector === "self") return false;
  return true;
}
